import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { isEmpty, prop } from 'ramda';

import { setDepositLimitConfig } from '@interlayer/js/app/store/reducers/ResponsibleGaming/actions';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';

import { useDepositLimits } from 'js/app/components/main/ResponsibleGaming/hooks/useDepositLimits';
import { useDynamicTags } from 'js/app/hooks/useDynamicTags';
import { useResponsibleGamingSelector } from 'js/app/store/hooks/useResponsibleGamingSelector';
import { inRange } from 'js/app/utils/common.util';
import { useTranslations } from 'js/hooks/i18n';
import { useDataFetch } from 'js/hooks/useDataFetch';
import { useFormatter } from 'js/hooks/useFormatter';
import { useUserSelector } from 'js/store/hooks';

export const useDepositLimitsConfig = () => {
    const currencyCode = useUserSelector(prop('currencyCode'));
    const depositLimitsFetch = useDepositLimits();
    const dispatch = useDispatch();
    const { formatCurrency } = useFormatter();
    const { depositLimitsConfig } = useResponsibleGamingSelector();
    const t = useTranslations('playtech.mobile.system.depositlimit.');
    const [
        dTagRangesDepositLimit = '',
        dTagDailyDefaultDepositLimit = '0',
        dTagWeeklyDefaultDepositLimit = '0',
        dTagMonthlyDefaultDepositLimit = '0'
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.rangesDepositLimit,
        DYNAMIC_TAG_NAMES.dailyDefaultDepositLimit,
        DYNAMIC_TAG_NAMES.weeklyDefaultDepositLimit,
        DYNAMIC_TAG_NAMES.monthlyDefaultDepositLimit
    ]);
    const defaultsDepositLimit = useRef([false, false, false]);

    const onResponseDepositLimitsConfig = useCallback(({ data }) => {
        const arrayDefaultDepositLimit = [
            Number(dTagDailyDefaultDepositLimit),
            Number(dTagWeeklyDefaultDepositLimit),
            Number(dTagMonthlyDefaultDepositLimit)
        ];

        const allowedLimits = data.predefinedLimits?.[0].allowedLimits || [];

        const rangesDepositLimitArr = dTagRangesDepositLimit.split(',').map(range => {
            if (!Number.isFinite(Number(range))) {
                return range.split('-').map(Number);
            }

            // for no limit
            if (range === '0' && allowedLimits.length) {
                allowedLimits.push(range);
            }

            return Number(range);
        });

        const allowedLimitsOptions = !dTagRangesDepositLimit ? allowedLimits : allowedLimits
            .reduce((arr, value) => {
                const limit = Number(value);

                for (let index = 0; index < rangesDepositLimitArr.length; index++) {
                    const range = rangesDepositLimitArr[index];

                    if (Array.isArray(range) && inRange(limit, ...range)) {
                        arr[index].push(limit);
                        return arr;
                    }

                    if (limit === range) {
                        arr[index].push(limit);
                        return arr;
                    }
                }

                return arr;
                // eslint-disable-next-line no-array-constructor
            }, Array.from({ length: rangesDepositLimitArr.length }, () => []))
            .reduce((arr, limit, index) => {
                const setOptions = (value) => {
                    const indexDefaultLimit = arrayDefaultDepositLimit.indexOf(value);

                    if (indexDefaultLimit > -1) {
                        defaultsDepositLimit.current[indexDefaultLimit] = true;
                    }

                    arr.push({
                        value,
                        label: value ? formatCurrency(value, { precision: 0 }, currencyCode) : t('noLimit')
                    });
                };

                if (!dTagRangesDepositLimit) {
                    setOptions(limit);

                    return arr;
                }

                limit.sort((a, b) => a - b);

                if (rangesDepositLimitArr[index][0] > rangesDepositLimitArr[index][1]) {
                    limit.reverse();
                }

                limit.forEach(setOptions);

                return arr;
            }, []);

        const checkDefaultLimit = (index) => {
            if (defaultsDepositLimit.current[index]) {
                return arrayDefaultDepositLimit[index];
            }

            return rangesDepositLimitArr.includes(0)
                ? 0
                : allowedLimitsOptions[0];
        };

        const response = {
            ...data,
            allowedLimits,
            allowedLimitsOptions,
            dTagDailyDefaultDepositLimit: checkDefaultLimit(0),
            dTagWeeklyDefaultDepositLimit: checkDefaultLimit(1),
            dTagMonthlyDefaultDepositLimit: checkDefaultLimit(2)
        };

        dispatch(setDepositLimitConfig(response));

        return response;
    }, [
        dTagDailyDefaultDepositLimit, dTagWeeklyDefaultDepositLimit, dTagMonthlyDefaultDepositLimit,
        dTagRangesDepositLimit, dispatch, formatCurrency, currencyCode, t
    ]);

    return useDataFetch({
        initialIsLoading: isEmpty(depositLimitsConfig),
        fetch: depositLimitsFetch,
        initialData: depositLimitsConfig,
        params: depositLimitsConfig,
        shouldFetch: isEmpty,
        onResponse: onResponseDepositLimitsConfig
    });
};

export const checkAvailablePeriod = (period, depositLimitsConfig) =>
    depositLimitsConfig?.availableTimePeriods?.includes(period) &&
    (depositLimitsConfig.allowedLimits?.length || !depositLimitsConfig.isEnabled);

